import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { I18n } from "aws-amplify/utils";
import { Authenticator, ThemeProvider, translations } from "@aws-amplify/ui-react";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App.js";

import { DBConfig } from "./DBConfig.ts";
import { initDB } from "react-indexed-db-hook";

// autoUpdate + skipWaiting: reload when a new SW takes control (avoids hashed-chunk white screens)
registerSW({ immediate: true });

// check if an element with a key of "EIFClearDB" is over a day old
// if so, delete the database
try {
  const clearDB = localStorage.getItem("EIFClearDB");
  const now = new Date();
  if (clearDB) {
    const clearDBDate = new Date(clearDB);
    if (now.getTime() - clearDBDate.getTime() > 86400000) {
      localStorage.setItem("EIFClearDB", `${now.getTime()}`);
      indexedDB.deleteDatabase(DBConfig.name);
    }
  } else {
    localStorage.setItem("EIFClearDB", `${now.getTime()}`);
    indexedDB.deleteDatabase(DBConfig.name);
  }

  initDB(DBConfig);
} catch (dbError) {
  // Some browsers (e.g. Orion / strict WebKit private mode) can block IndexedDB.
  // Do not prevent the app from mounting — offline cache is optional.
  console.warn('IndexedDB init failed; continuing without local DB.', dbError);
}

// Gen 2: Use amplify_outputs.json instead of aws-exports.js
// Make sure to run: npm run amplify:sandbox first to generate this file
import outputs from "../amplify_outputs.json";
import { runMigrationGateOnStartup } from "./utils/migrationIssuerGate";

import "@aws-amplify/ui-react/styles.css";
import "./index.css";

import * as Sentry from "@sentry/react";
import { AppProvider } from "./contexts/AppContext.tsx";
import { NavigationGuardProvider } from "./contexts/NavigationGuardContext.tsx";
import { UserProvider } from "./contexts/UserContext";
import { CompanyProvider } from "./contexts/CompanyContext.tsx";
import { SubscriptionProvider } from "./contexts/SubscriptionContext.tsx";


Sentry.init({
  dsn: "https://5cb9ea23d9e245efe15ec59265627216@o4507421376970752.ingest.us.sentry.io/4507421379461120",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "equipmentinspectionforms.com",
    /^https:\/\/amazonaws\.com\/graphql/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: process.env.NODE_ENV !== 'development',
});

Amplify.configure(outputs);

// Amplify UI's default password-policy copy ("Password must have upper case
// letters") reads as if every letter must be uppercase. Override to match the
// actual Cognito rule: at least one of each required character class.
I18n.putVocabularies(translations);
I18n.putVocabulariesForLanguage("en", {
  "Password must have upper case letters":
    "Password must have at least 1 upper case letter",
  "Password must have lower case letters":
    "Password must have at least 1 lower case letter",
  "Password must have numbers": "Password must have at least 1 number",
  "Password must have special characters":
    "Password must have at least 1 special character",
});

// Bootstrap: run the migration gate to completion BEFORE React mounts.
//
// The gate (sweepOrphanedOldPoolTokens + enforceMigrationIssuerGate) clears
// stale OLD-pool tokens / sessions left over from the Sept 2026 Cognito
// cross-account cutover. Previously this was fire-and-forget
// (`void runMigrationGateOnStartup()`), so React rendered immediately and a
// protected route (e.g. /equipment) could call fetchAuthSession() while a
// stale old-pool idToken was still in localStorage. That idToken fails the
// Identity Pool exchange with `NotAuthorizedException: Invalid login token.
// Couldn't verify signed token.` (Sentry e80b4ab8). Awaiting the gate first
// guarantees the sweep + any forced sign-out finish before fetchAuthSession()
// can run against a stale token. Inert when the frontend is pinned to an old
// pool (rollback). See src/utils/migrationIssuerGate.ts.
async function bootstrap() {
  try {
    await runMigrationGateOnStartup();
  } catch (err) {
    // Never block app mount on the gate — a failed sweep should degrade to the
    // pre-fix behavior, not a blank screen.
    console.warn("[migration] startup gate failed, mounting anyway:", err);
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <Authenticator.Provider>
        <ThemeProvider>
          <UserProvider>
            <CompanyProvider>
              <SubscriptionProvider>
                <AppProvider>
                  <Router>
                    <NavigationGuardProvider>
                      <App />
                    </NavigationGuardProvider>
                  </Router>
                </AppProvider>
              </SubscriptionProvider>
            </CompanyProvider>
          </UserProvider>
        </ThemeProvider>
      </Authenticator.Provider>
    </React.StrictMode>
  );
}

void bootstrap();
